import { useState } from 'react';
import { ConfirmSheet } from '@shared/ui/containers/ConfirmSheet';
import type { AccessInvite } from './api';

interface UseAccessInviteCancelConfirmOptions {
  onConfirm: (accessInviteId: string) => void | Promise<void>;
  loading?: boolean;
}

/**
 * Opens a ConfirmSheet when the member swipes/taps cancel on an access invite.
 */
export function useAccessInviteCancelConfirm({
  onConfirm,
  loading = false,
}: UseAccessInviteCancelConfirmOptions): {
  requestCancel: (invite: AccessInvite) => void;
  cancelConfirmSheet: JSX.Element;
} {
  const [pending, setPending] = useState<AccessInvite | null>(null);

  const visitorLabel = pending?.visitor_name?.trim() || 'this visitor';
  const reference = pending?.reference?.trim();

  const cancelConfirmSheet = (
    <ConfirmSheet
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open && !loading) setPending(null);
      }}
      title="Cancel invite?"
      description={
        reference
          ? `This will revoke the pass for ${visitorLabel} (${reference}).`
          : `This will revoke the pass for ${visitorLabel}.`
      }
      dismissLabel="Keep invite"
      confirmLabel="Cancel invite"
      loading={loading}
      onConfirm={() => {
        if (!pending) return;
        void (async () => {
          await onConfirm(pending.access_invite_id);
          setPending(null);
        })();
      }}
    />
  );

  return {
    requestCancel: setPending,
    cancelConfirmSheet,
  };
}
